import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, input, signal, WritableSignal, Signal, ModelSignal, model, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, FormRecord, FormsModule, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { DateTime } from 'luxon';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputNumberModule } from 'primeng/inputnumber';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SkeletonModule } from 'primeng/skeleton';
import { TextareaModule } from 'primeng/textarea';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MinimalStaff, TripDetail, TripDetailService, TripStaffingService } from 'src/app/api';
import { FormManagementService } from 'src/app/api/form-management.service';
import { FormResponseService } from 'src/app/api/form-response.service';
import { FormOption, FormQuestion, FormQuestionResponse, FormQuestionTypes, FormResponse, Locales, NumericQuestionTypes, PpmFormWithQuestions, TextQuestionTypes } from 'src/app/api/types/forms';
import { ErrorService } from 'src/app/services/error.service';
import { GetTranslationPipe } from 'src/app/ui/pipes/get-translation.pipe';
import { TranslationService } from 'src/app/translation-service.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { Location } from '@angular/common';
import { NavigationService } from 'src/app/utility/navigation.service';
import { IconService } from 'src/app/ui/icon.service';


interface QuestionOptionFormGroup {
  id: FormControl<number>,
  text: FormControl<string>,
  selected: FormControl<boolean>
}

interface QuestionResponseFormGroup {
  id: FormControl<number>,
  formQuestionId: FormControl<number>,
  formResponseId: FormControl<number>,
  text: FormControl<string | null>,
  value: FormControl<number | null>,
  staffId: FormControl<number | null>,
  optionId: FormControl<number | null>,
  optionText: FormControl<string | null>,
  options: FormArray<FormGroup<QuestionOptionFormGroup>>

}

@Component({
  selector: 'app-form-response',
  imports: [CardModule, ReactiveFormsModule, FormsModule, TextareaModule, ButtonModule, RadioButtonModule, InputNumberModule, SkeletonModule, GetTranslationPipe, SelectModule, RadioButtonModule, InputTextModule, CheckboxModule, FontAwesomeModule],
  templateUrl: './form-response.component.html',
  styleUrl: './form-response.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormResponseComponent implements OnInit {

  responseId = input(0)
  response: WritableSignal<FormResponse> = signal(this.formResponseService.getEmptyResponse())
  form: WritableSignal<PpmFormWithQuestions> = signal(this.formManagementService.getEmptyForm())
  newId = 0
  alreadySubmitted = signal(false)
  submitted = signal(false)
  staff: Array<MinimalStaff & { type: "tl" | "atl" }> = []
  staffMap: Map<number, MinimalStaff & { type: "tl" | "atl" }> = new Map()
  tripDetails: WritableSignal<TripDetail[]> = signal([])
  submitting = signal(false)
  showGoogleReviewLink = signal(false)

  locale: ModelSignal<string> = model("en")
  localeOptions = [{
    label: "English",
    value: "en"
  },
  {
    label: "Español",
    value: "es"
  }]

  availableLocales: WritableSignal<typeof this.localeOptions> = signal([])

  showLocaleDropdown = computed<boolean>(() => {
    return this.availableLocales().length > 1
  })


  strings = {
    'trip-date': "Trip Date",
    'groups': "Group(s)",
    'language': "Language",
    'missing-responses': "There are missing responses above",
    'already-submitted': "You have already submitted this form",
    'response-submitted': "Thank you for your response! You may now close this page.",
    'google-review-link': "If you have another 60 seconds, would you be willing sharing your experience with PPM on Google as well? It helps us immensely.",
    'leave-review-on-google': "Leave a Review on Google",
    'submit': "Submit",
    translations: [
      {
        locale: "es",
        'trip-date': "Fecha de viaje",
        'groups': "Grupo(s)",
        'language': "Idioma",
        'missing-responses': "Faltan respuestas arriba.",
        'already-submitted': "Ya has enviado este formulario",
        'response-submitted': "¡Gracias por tu respuesta! Puedes cerrar esta página ahora.",
        'google-review-link': "Si tienes un minuto, ¿te gustaría compartir tu experiencia con PPM en Google? ¡Nos ayuda mucho!",
        'leave-review-on-google': "Dejar una reseña en Google",
        'submit': "Enviar"
      }
    ]
  }

  tripDateRange: Signal<string> = computed(() => {
    const details = this.tripDetails()

    if (details.length > 0) {

      const startDates = details
        .map(t => t.tripDate)
        .sort((a, b) => a.valueOf() - b?.valueOf())

      const endDates = details
        .map(t => t.tripEndDate)
        .sort((a, b) => a.valueOf() - b?.valueOf())


      const startDateString = DateTime.fromJSDate(startDates[0]).toLocaleString()
      const endDateString = DateTime.fromJSDate(endDates[endDates.length - 1]).toLocaleString()

      return `${startDateString} - ${endDateString}`

    }
    else {
      return ''
    }
  })

  groupNames = computed(() => {
    const details = this.tripDetails()

    if (details.length > 0) {
      return details.map(t => t.groupName)
    }
    else {
      return []
    }

  })






  loading = signal(false)

  questionMap = computed(() => {
    const questions = this.form().questions

    const map = new Map<number, FormQuestion>()

    for (const question of questions) {
      map.set(question.id, question)
    }

    return map
  })

  optionMap = computed(() => {
    const questions = this.form().questions
    const map = new Map<number, FormOption>()

    for (const question of questions) {
      for (const option of question?.options ?? []) {
        map.set(option.id, option)
      }
    }

    return map
  })

  questionTypes = FormQuestionTypes
  numericScale = [1, 2, 3, 4, 5]

  constructor(
    private formResponseService: FormResponseService,
    private formManagementService: FormManagementService,
    private errorService: ErrorService,
    private messageService: MessageService,
    private fb: FormBuilder,
    private tripStaffingService: TripStaffingService,
    private tripDetailService: TripDetailService,
    private chRef: ChangeDetectorRef,
    private translationService: TranslationService,
    private route: ActivatedRoute,
    public navigation: NavigationService,
    public icons: IconService,

  ) {
    effect(() => {
      this.loadResponse(this.responseId())
    })

  }
  ngOnInit(): void {

    this.route.queryParamMap.subscribe(params => {
      const selectedLocale = params.get("locale")

      if (selectedLocale && Locales.includes(selectedLocale)) {
        this.locale.set(selectedLocale)
      }

    })


  }


  responseForm = this.fb.group({
    questions: this.fb.nonNullable.array<FormGroup<QuestionResponseFormGroup>>([])
  })


  loadResponse(responseId: number) {

    if (responseId > 0) {
      this.loading.set(true)
      this.formResponseService.getFormResponse(responseId).subscribe({
        next: result => {
          this.response.set(result.response)
          this.form.set(result.form)

          let translationLocales = Array.from(new Set([
            "en",
            ...(result.form?.translations ?? []).map(t => t.locale),
            ...result.form.questions.flatMap(q => q.translations ? q.translations.map(t => t.locale) : [])
          ]))


          this.availableLocales.set(this.localeOptions.filter(o => translationLocales.includes(o.value)))

          if (result.response.isSubmitted === 1) {
            this.alreadySubmitted.set(true)
            this.loading.set(false)
          }
          else {
            if (result.response?.responseTrips && result.response.responseTrips.length > 0) {

              const tripIds = result.response.responseTrips.map(t => t.tripId)

              if (tripIds.length > 0) {


                this.tripStaffingService.getLimitedStaffForTrips(tripIds).subscribe(staffResponse => {
                  this.staff = staffResponse.staff.map(s => { return { ...s, type: staffResponse.tlIds.includes(s.id) ? "tl" : "atl" } })
                  this.staffMap = new Map(this.staff.map(s => [s.id, s]))
                  this.generateForm(result.form, result.response)
                  this.loading.set(false)
                })

                const tripDetails = this.tripDetailService.getTripsByIdList(tripIds)

                tripDetails.subscribe({
                  next: (result) => {
                    this.tripDetails.set(result)
                  }
                })
              }
            }
            else {
              this.generateForm(result.form, result.response)
              this.loading.set(false)
            }
          }

        },
        error: (err) => {
          this.errorService.error("There was a problem loading this form", err, this.messageService, "Error in form response get")
          this.loading.set(false)
        }
      })


    }

  }

  generateForm(form: PpmFormWithQuestions, response: FormResponse) {
    this.responseForm.controls.questions = this.fb.array<FormGroup<QuestionResponseFormGroup>>([])
    for (const q of form.questions) {
      if (q.type === FormQuestionTypes.AtlEval || q.type === FormQuestionTypes.TlEval) {

        for (const staff of this.staff) {
          if (
              (q.type === FormQuestionTypes.AtlEval && staff.type === "atl") ||
              (q.type === FormQuestionTypes.TlEval && staff.type === "tl")
          ) {
            this.addQuestionFormGroup(q, response.questionResponses ?? [], staff)
          }
        }
      }

      else {
        this.addQuestionFormGroup(q, response.questionResponses ?? [], undefined)
      }
    }

    this.chRef.detectChanges()
  }

  addQuestionFormGroup(question: FormQuestion, questionResponses: FormQuestionResponse[], staff: MinimalStaff | undefined) {
    const existingResponse = questionResponses.find(qr => qr.formQuestionId === question.id && qr.staffId === (staff?.id ?? null))

    //FUTURE:  Potential Code for implemening multiple select list.  Assign a form array to the response form and pass that to the server
    const formOptions = this.fb.array<FormGroup<QuestionOptionFormGroup>>([])


    if (question.type === FormQuestionTypes.MultiOptionSelect) {
      for (const option of question?.options ?? []) {
        const selected = !!existingResponse?.optionSelections?.find(s => s.formOptionId === option.id)

        formOptions.push(this.fb.group<QuestionOptionFormGroup>({
          id: this.fb.nonNullable.control(option.id),
          text: this.fb.nonNullable.control(option.text),
          selected: this.fb.nonNullable.control(selected)
        })
        )
      }
    }

    let selectedOption = null
    let selectedOptionText = null

    if (existingResponse?.optionSelections && existingResponse.optionSelections.length > 0) {
      selectedOption = existingResponse.optionSelections[0].formOptionId
      selectedOptionText = existingResponse.optionSelections[0].text
    }

    this.responseForm.controls.questions.push(this.fb.group<QuestionResponseFormGroup>({
      formQuestionId: this.fb.nonNullable.control<number>(question.id),
      formResponseId: this.fb.nonNullable.control<number>(this.response().id),
      id: this.fb.nonNullable.control<number>(existingResponse?.id ?? --this.newId),
      staffId: this.fb.control<number | null>(existingResponse?.staffId ?? (staff?.id ?? null), { nonNullable: false }),
      text: this.fb.control<string | null>(existingResponse?.text ?? null, { validators: this.getValidators("text", question) }),
      value: this.fb.control<number | null>(existingResponse?.value ?? null, { validators: this.getValidators("value", question) }),
      optionId: this.fb.control<number | null>(selectedOption, { validators: this.getValidators("optionId", question) }),
      optionText: this.fb.control<string | null>(selectedOptionText ?? null),
      options: formOptions

    }))
  }


  getValidators(field: keyof QuestionResponseFormGroup, question: FormQuestion) {
    let validators: ValidatorFn[] = []

    switch (field) {
      case "text":
        if (TextQuestionTypes.includes(question.type)) {
          if (question.required) {
            validators = [Validators.required]
          }
        }
        break
      case "value":
        if (NumericQuestionTypes.includes(question.type)) {
          if (question.required) {
            validators = [Validators.required]
          }
        }
        break
      case "optionId":
        if (question.type === FormQuestionTypes.SingleOptionSelect) {
          if (question.required) {
            validators = [Validators.required]
          }
        }
        break

    }

    return validators
  }

  submitForm() {

    this.submitting.set(true)
    //console.log(this.responseForm.getRawValue())

    this.responseForm.markAllAsTouched()


    if (this.responseForm.controls.questions.valid) {
      const answers = this.responseForm.controls.questions.getRawValue()

      this.formResponseService.submitForm(this.response().id, answers).subscribe({
        next: (result) => {
          this.submitted.set(true)
          this.submitting.set(false)
          this.showGoogleReviewLinkAfterSubmit(answers)

          if (!this.showGoogleReviewLink() && this.navigation.canGoBack()) {
            this.navigation.back()
          }
          
        },
        error: (err) => {
          this.submitting.set(false)
          this.errorService.error("There was a problem saving", err, this.messageService)
        }
      })
    }
    else {
      this.submitting.set(false)
      this.messageService.add({ severity: "warn", detail: this.translationService.getTranslation(this.strings, 'missing-responses', this.locale()) })
    }
  }

  private showGoogleReviewLinkAfterSubmit(
    answers: Array<{ formQuestionId: number; value: number | null }>,
  ): void {
    const overallQuestion = this.form().questions.find(
      (q) => q.reportText.trim().toLowerCase() === 'overall',
    );
    if (!overallQuestion) {
      return;
    }

    const answer = answers.find((a) => a.formQuestionId === overallQuestion.id);
    if (answer?.value === 5) {
      this.showGoogleReviewLink.set(true);
    }
  }
}
